import { useNotification } from 'admin';
import {
  getApiDocumentsV1DocumentsPdfCreateMutationOptions,
  getApiDocumentsV1DocumentsRetrieveQueryKey,
  useApiDocumentsV1DocumentsPdfCreate,
  useApiDocumentsV1DocumentsRetrieve,
  useQueryClient,
} from 'api';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CenteredLoader, Divider, PdfViewer, Sidebar, styled, Typography, useBreakpoint } from 'ui';

import { ActionButtons, DocumentList, Header } from '../../components';
import { DocumentProvider } from '../../context/DocumentContext';

const StretchedPdfViewer = styled(PdfViewer)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
}));

export const DocumentPage = () => {
  const { id: idParam } = useParams();
  const queryClient = useQueryClient();
  const {
    data: document,
    isLoading,
    error,
  } = useApiDocumentsV1DocumentsRetrieve(Number(idParam), {
    query: {
      enabled: Boolean(idParam),
    },
  });
  const { open } = useNotification();
  const { isMobile } = useBreakpoint();
  const [isSidebarOpen, setIsSidebarOpen] = useState(isMobile ? false : true);
  const {
    mutate: generatePdf,
    isPending: isGeneratePdfPending,
    data,
  } = useApiDocumentsV1DocumentsPdfCreate({
    mutation: {
      mutationKey: [getApiDocumentsV1DocumentsPdfCreateMutationOptions().mutationKey, 'generate'],
      onError: () => {
        open?.({ message: 'Failed to generate pdf', type: 'error' });
      },
      onSuccess: () => {
        if (!document) {
          return;
        }

        queryClient.invalidateQueries({
          queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
        });
      },
    },
  });

  const {
    mutate: savePdf,
    isPending: isSavingPdf,
    data: savedData,
    reset: resetSaveMutation,
  } = useApiDocumentsV1DocumentsPdfCreate({
    mutation: {
      mutationKey: [getApiDocumentsV1DocumentsPdfCreateMutationOptions().mutationKey, 'save'],
      onError: () => {
        open?.({ message: 'Failed to generate pdf', type: 'error' });
      },
      onSuccess: () => {
        if (!document) {
          return;
        }

        queryClient.invalidateQueries({
          queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(document.id),
        });
      },
    },
  });

  if (isLoading) {
    return <CenteredLoader />;
  }

  if (!document) {
    return <EmptyState />;
  }

  const url = data?.url ?? document.file_url;

  const disableSaveButton = Boolean(data?.url && (!savedData?.url || isSavingPdf));

  return (
    <DocumentProvider document={document} isLoading={isLoading} error={error}>
      <Sidebar.Container isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen((open) => !open)}>
        <Box height="100%" display="flex" flexDirection="column">
          <Header />
          <Divider />
          <ActionButtons
            generatePdf={generatePdf}
            savePdf={savePdf}
            isSavingPdf={isSavingPdf}
            isGeneratePdfPending={isGeneratePdfPending}
            disableSaveButton={disableSaveButton}
            resetSaveMutation={resetSaveMutation}
          />
          {document && url !== null ? <StretchedPdfViewer title="Document" src={url} /> : <EmptyState />}
        </Box>
        <Sidebar isOpen={isSidebarOpen}>
          <DocumentList contextObjectId={document.context_object_id} />
        </Sidebar>
      </Sidebar.Container>
    </DocumentProvider>
  );
};

const EmptyState = () => {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4}>
      <Typography variant="h4" align="center">
        Document does not exist
      </Typography>
    </Box>
  );
};
